import { useLayoutEffect, useMemo, useRef } from "react"
import { useAppSelector } from "hooks/redux"
import { factorsViewSlice, DEFAULT_FACTORS_VIEW_STATE, FACTORS_VIEW_PREFERENCE_BASE, selectFactorsViewPanel } from "connected-components/dashboard/store/factors-view-slice"
import { useDashboardSliceData } from "connected-components/dashboard/hooks/use-dashboard-slice-data"
import { getToday } from "helpers/types/dates"
import { resolveDateOverride, getEffectiveLatestDate, type DateOverride } from "connected-components/dashboard/helpers/date-override"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyValue = any

interface FactorsViewModuleConfig {
  initialView?: string
  sortMetricId?: string | null
  selectedTableLevels?: string[]
  dateOverride?: DateOverride | null
}

/**
 * Panel-scoped factors-view data + actions, read directly from RTK. Pass moduleConfig only from the
 * top-level wrapper; the seed is idempotent, so extra calls from children are safe.
 */
export function useFactorsViewData(moduleConfig?: FactorsViewModuleConfig) {
  const { dashboardId, panelKey, dispatch, modelId, core, trade } = useDashboardSliceData(FACTORS_VIEW_PREFERENCE_BASE)

  const factorsViewState = useAppSelector(
    (s) => selectFactorsViewPanel(s, dashboardId, panelKey) ?? DEFAULT_FACTORS_VIEW_STATE,
  )

  // Layout effect (registered before the sync's usePreferences) so the seed wins even when a
  // cache-warm fallback resolves synchronously on mount and omits the seeded fields.
  const seeded = useRef(false)
  useLayoutEffect(() => {
    if (seeded.current) return
    // Wait for config before latching, or a config arriving on a later render never seeds.
    if (moduleConfig == null) return
    seeded.current = true
    dispatch(factorsViewSlice.actions.initFromModuleConfig({
      dashboardId,
      panelId: panelKey,
      view: moduleConfig.initialView,
      sortMetricId: moduleConfig.sortMetricId,
      selectedTableLevels: moduleConfig.selectedTableLevels,
      dateOverride: moduleConfig.dateOverride,
    }))
  }, [dashboardId, panelKey, dispatch, moduleConfig])

  const updaters = useMemo(() => ({
    setView: (view: string) => dispatch(factorsViewSlice.actions.setView({ dashboardId, panelId: panelKey, view })),
    setGroupBy: (groupBy: AnyValue) => dispatch(factorsViewSlice.actions.setGroupBy({ dashboardId, panelId: panelKey, groupBy })),
    setShowNotHeld: (showNotHeld: boolean | null) => dispatch(factorsViewSlice.actions.setShowNotHeld({ dashboardId, panelId: panelKey, showNotHeld })),
    setShowMetricsSelector: (showMetricsSelector: boolean) => dispatch(factorsViewSlice.actions.setShowMetricsSelector({ dashboardId, panelId: panelKey, showMetricsSelector })),
    setSelectedPeriodPerformance: (selectedPeriodPerformance: string) => dispatch(factorsViewSlice.actions.setSelectedPeriodPerformance({ dashboardId, panelId: panelKey, selectedPeriodPerformance })),
    setSortMetricId: (sortMetricId: string | null) => dispatch(factorsViewSlice.actions.setSortMetricId({ dashboardId, panelId: panelKey, sortMetricId })),
    setSortDirection: (sortDirection: "ASC" | "DESC") => dispatch(factorsViewSlice.actions.setSortDirection({ dashboardId, panelId: panelKey, sortDirection })),
    setSelectedTableLevels: (selectedTableLevels: string[]) => dispatch(factorsViewSlice.actions.setSelectedTableLevels({ dashboardId, panelId: panelKey, selectedTableLevels })),
    setDatagridState: (datagridState: AnyValue) => dispatch(factorsViewSlice.actions.setDatagridState({ dashboardId, panelId: panelKey, datagridState })),
    setDateOverride: (dateOverride: DateOverride) =>
      dispatch(factorsViewSlice.actions.setDateOverride({ dashboardId, panelId: panelKey, dateOverride })),
    clearDateOverride: () =>
      dispatch(factorsViewSlice.actions.clearDateOverride({ dashboardId, panelId: panelKey })),
  }), [dispatch, dashboardId, panelKey])

  // Spread last (below) so it wins over the raw core dates — see use-graph-data.ts.
  const effectiveDates = useMemo(() => {
    const { dateOverride } = factorsViewState
    const effectiveLatestDate = getEffectiveLatestDate(core.isIntraday, core.endDate, core.latestDate)
    const resolved = dateOverride ? resolveDateOverride(dateOverride, core.earliestDate, effectiveLatestDate) : null
    const endDate = resolved?.endDate ?? core.endDate
    return {
      startDate: resolved?.startDate ?? core.startDate,
      endDate,
      performanceStartDate: resolved?.performanceStartDate ?? core.performanceStartDate,
      performanceEndDate: resolved?.endDate ?? core.performanceEndDate,
      period: resolved != null && dateOverride != null ? dateOverride.period : core.period,
      isDateOverrideActive: resolved != null,
      isIntradayMode: core.isIntraday && endDate === getToday(),
    }
  }, [factorsViewState.dateOverride, core.isIntraday, core.earliestDate, core.latestDate, core.startDate, core.endDate, core.performanceStartDate, core.performanceEndDate, core.period])

  return useMemo(() => ({
    ...core,
    modelId,
    ...trade,
    ...factorsViewState,
    ...effectiveDates,
    ...updaters,
  }), [core, modelId, trade, factorsViewState, effectiveDates, updaters])
}
